import React, { useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import { getSizedImageUrl } from '../../../../../scripts/utils/image';

import { useSubBuilder } from '../../../../context/subBuilder/useSubBuilder';
import { aysncGetRCProductOptions, findKgVariantOnProduct } from '../../../../context/subBuilder/utils';
import { ProductVariant } from '../../../../util/typings';
import Money from '../../../Money';
import Price from '../../../Price';
import Calculator from '../components/Calculator';
import GreyBoxYellowBoxSwitcheroo from '../components/GreyBoxYellowBoxSwitcheroo';

import SubBuilderStep from '../components/SubBuilderStep';


const Routine: React.FunctionComponent = () => {
  const { state, settings, actions, getters } = useSubBuilder();

  const variant = getters.getCurrentVariant();

  const [currentVariant, setCurrentVariant] = useState(null as ProductVariant);

  const [calculatorOpen, setCalculatorOpen] = useState(false);

  const [options, setOptions] = useState([] as number[]);
  const [unit, setUnit] = useState(null as string);

  useEffect(() => {
    const variant = getters.getCurrentVariant();
    if (variant) {
      setCurrentVariant(variant);
    }
  }, [state]);

  useEffect(() => {
    aysncGetRCProductOptions(state.productHandle).then((setup) => {
      setOptions(setup.amounts);
      setUnit(setup.unit);
    });
  }, [state.productHandle]);

  useEffect(() => {
    if (options.length > 0) {
      actions.setIntervalFrequency(options[0]);
    }
  }, [options]);

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', calculatorOpen);
  }, [calculatorOpen]);

  const typeOfOptions = state.isBothProducts
    ? 'two_x_quantity_option'
    : 'quantity_option';

  const routineOptions = settings.steps.routine.options.filter(
    ({ type }) => type === typeOfOptions,
  );
  if (!routineOptions || routineOptions.length < 1) {
    console.error(
      `No quantity options set, please make sure you add block of type ${typeOfOptions} in the settings.`,
    );
  }

  const currentProduct = settings.products.me.find(
    p => p.product.handle === state.productHandle
  )?.product || null;

  const getKgVariantForQuantity = (quantity: number): typeof variant | null => {
    if (!currentProduct || quantity % 4 !== 0) return null;
    return findKgVariantOnProduct(currentProduct, state.grindOption);
  };

  const getPrice = (basePrice: number, quantity: number) => {
    const kgVar = getKgVariantForQuantity(quantity);
    if (kgVar) return kgVar.price * (quantity / 4);
    return basePrice * quantity;
  };

  return (
    <>
      <SubBuilderStep
        index={4}
        className="sub-builder-routine"
        stepConfig={settings.steps.routine}
      >
        <div className="sub-builder-quantity-label-calc">
          <label className="sub-builder-quantity-label">
            {settings.steps.routine.strings.quantityLabel}
          </label>
          <div>
            <label className="sub-builder-quantity-calc-label">
              {settings.steps.routine.strings.calculatorLabel}
            </label>
            <button
              onClick={() => setCalculatorOpen(true)}
              className="sub-builder-quantity-calc-cta"
            >
              {settings.steps.routine.strings.calculatorCta}
            </button>
          </div>
        </div>
        <div className="sub-builder-quantity-options">
          {routineOptions.map((option, i) => {
            const { image, kgImage, quantity, title } = option;
            const basePrice = variant?.price || 0;
            const kgVar = getKgVariantForQuantity(quantity);
            const hasKgDiscount = !!kgVar && basePrice > 0 && kgVar.price < basePrice * 4;
            const finalPrice = getPrice(basePrice, quantity);
            const originalPrice = kgVar ? basePrice * quantity : finalPrice;
            const kgBagCount = (quantity / 4) * (state.isBothProducts ? 2 : 1);
            const displayTitle = kgVar
              ? `${kgBagCount} x 1kg bag${kgBagCount > 1 ? 's' : ''}`
              : title;
            const displayImage = kgVar && kgImage ? kgImage : image;
            return (
              <button
                id={`sub-builder-btn-routine-${i}`}
                className={`sub-builder-btn-quantity sub-builder-btn ${quantity === state.quantityOption ? 'is-active' : ''}`}
                key={quantity}
                type="button"
                onClick={() => {
                  actions.setQuantityOption(quantity);
                  if (options && options.length === 1) {
                    actions.moveStep(+1);
                  }
                }}
              >
                <GreyBoxYellowBoxSwitcheroo className="sub-builder-btn-img sub-builder-quantity-img">
                  <img
                    src={getSizedImageUrl(displayImage, '300x300')}
                    data-lazy-load
                  />
                </GreyBoxYellowBoxSwitcheroo>
                {hasKgDiscount && (
                  <span className="sub-builder-discount-badge">
                    15% OFF
                  </span>
                )}
                <h3 className="sub-builder-btn-title">
                  {displayTitle}
                </h3>
                <Price
                  finalPrice={finalPrice}
                  originalPrice={originalPrice}
                  priceVaries={false}
                />
              </button>
            );
          })}
        </div>
        <>
          {options && options.length > 1 && (
            <div className="sub-builder-quantity-label-calc">
              <label className="sub-builder-quantity-label">
                {settings.steps.routine.strings.frequencyLabel}
              </label>
              <div>
                <label className="sub-builder-quantity-calc-label">
                  {settings.steps.routine.strings.calculatorLabel}
                </label>
                <button
                  onClick={() => setCalculatorOpen(true)}
                  className="sub-builder-quantity-calc-cta"
                >
                  {settings.steps.routine.strings.calculatorCta}
                </button>
              </div>
            </div>
          )}
          {options && options.length > 1 && (
            <div className="select-wrapper">
              <select
                value={state.intervalFrequency ?? ''}
                className="select"
                onChange={(e) => {
                  actions.setIntervalFrequency(parseInt(e.target.value));
                }}
              >
                {options.map((option) => (
                  <option key={option} value={option}>
                    {option} {`${unit}${option > 1 ? 's' : ''}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          {options && options.length === 1 && (
            <div>
              <div className="single-option-section">
                <h4 className="single-option-title heading-5">
                  {settings.steps.routine.strings.singleOptionTitle}
                </h4>
                <div className="single-option-text">
                  {ReactHtmlParser(
                    settings.steps.routine.strings.singleOptionText,
                  )}
                </div>
              </div>
            </div>
          )}
          {options &&
            options.length > 1 &&
            currentVariant &&
            state.intervalFrequency &&
            state.quantityOption && (
            <div className="flex space-between align-center continue-section">
              <span className="continue-section-price">
                <Money
                  amount={getPrice(
                    currentVariant.price,
                    state.quantityOption,
                  )}
                />{' '}
                {settings.steps.routine.strings.everyLabel}{' '}
                {state.intervalFrequency}{' '}
                {`${unit}${state.intervalFrequency > 1 ? 's' : ''}`}
              </span>
              <button
                className="btn btn--primary"
                type="button"
                onClick={() => actions.moveStep(+1)}
              >
                  Continue
              </button>
            </div>
          )}
        </>
      </SubBuilderStep>
      {calculatorOpen && (
        <Calculator
          open={calculatorOpen}
          onClose={() => setCalculatorOpen(false)}
        />
      )}
    </>
  );
};
export default Routine;
